<template>
  <div
    class="relative flex items-center justify-center w-full overflow-hidden px-4"
    style="min-height: 100dvh"
    :class="isDark ? 'bg-stone-950' : 'bg-[#f5efe6]'"
  >
    <!-- Decorative background -->
    <div class="pointer-events-none absolute inset-0 overflow-hidden">
      <div
        class="absolute -top-24 -left-24 w-md h-112 rounded-full blur-3xl"
        :class="isDark ? 'bg-amber-500/10' : 'bg-amber-300/30'"
      />
      <div
        class="absolute -bottom-32 -right-24 w-lg h-128 rounded-full blur-3xl"
        :class="isDark ? 'bg-stone-100/5' : 'bg-orange-200/30'"
      />
      <div
        class="absolute inset-0"
        :class="isDark
          ? 'bg-linear-to-b from-transparent via-transparent to-[#1c1714]'
          : 'bg-linear-to-b from-transparent via-transparent to-[#efe6d8]'"
      />
    </div>

    <!-- Card -->
    <div
      class="relative z-10 w-full max-w-lg rounded-3xl border shadow-2xl backdrop-blur-md px-8 py-10 sm:px-12 sm:py-12 flex flex-col items-center text-center"
      :class="isDark
        ? 'bg-stone-900/60 border-stone-700/70 shadow-black/40'
        : 'bg-white/70 border-stone-200 shadow-stone-300/40'"
    >
      <!-- Logo -->
      <img :src="logoSrc" alt="Gioele Monni" class="h-9 sm:h-10 w-auto mb-8 opacity-90" />

      <!-- Status number -->
      <div class="relative">
        <span
          class="block font-black leading-none tracking-tighter select-none text-7xl sm:text-8xl bg-clip-text text-transparent"
          :class="isDark
            ? 'bg-linear-to-b from-stone-100 to-stone-500'
            : 'bg-linear-to-b from-stone-700 to-stone-900'"
        >
          {{ statusCode }}
        </span>
      </div>

      <!-- Eyebrow -->
      <div
        class="mt-5 inline-flex items-center gap-2 rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-widest"
        :class="isDark
          ? 'bg-amber-500/10 text-amber-300 ring-1 ring-amber-500/20'
          : 'bg-amber-100 text-amber-700 ring-1 ring-amber-200'"
      >
        <i :class="errorIcon" class="text-xs" />
        {{ $t(title) }}
      </div>

      <!-- Subtitle -->
      <p
        class="mt-4 text-sm sm:text-base leading-relaxed max-w-sm"
        style="white-space: pre-line"
        :class="isDark ? 'text-stone-400' : 'text-stone-500'"
      >
        {{ $t(subTitle) }}
      </p>

      <!-- Divider -->
      <div
        class="my-8 h-px w-16"
        :class="isDark ? 'bg-stone-700' : 'bg-stone-200'"
      />

      <!-- CTA -->
      <div class="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto">
        <button
          v-if="statusCode >= 500"
          class="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-semibold border transition-transform hover:scale-[1.03]"
          :class="isDark
            ? 'border-stone-600 text-stone-300 hover:border-stone-400 hover:text-white'
            : 'border-stone-300 text-stone-600 hover:border-stone-500 hover:text-stone-900'"
          @click="retryPage"
        >
          <i class="fal fa-rotate-right" /> Riprova
        </button>
        <NuxtLink :to="destination" class="w-full sm:w-auto">
          <Button
            :label="$t(buttonLabelKey)"
            icon="fal fa-arrow-left"
            class="w-full rounded-full! px-6! py-3! font-semibold! border-0! transition-transform hover:scale-[1.03]"
            :class="isDark
              ? 'bg-white! text-stone-900! hover:bg-stone-100!'
              : 'bg-stone-900! text-white! hover:bg-stone-800!'"
          />
        </NuxtLink>
      </div>
    </div>

    <!-- Wordmark footer -->
    <div
      class="absolute bottom-6 left-0 right-0 text-center text-[11px] tracking-widest uppercase"
      :class="isDark ? 'text-stone-600' : 'text-stone-400'"
    >
      Gioele Monni · Photography
    </div>
  </div>
</template>

<script setup>
import { useLayout } from './layouts/composables/layout';

const { layoutConfig } = useLayout();
const { $localStorage } = useNuxtApp();
const props = defineProps(['error']);
const env = useRuntimeConfig();
const PROJECT_BASE = env.public.PROJECT_BASE;
const token = useCookie(`token${env.public.PROJECT_BASE}`).value;

const isDark = computed(() => layoutConfig.isDarkTheme);
const statusCode = computed(() => props.error?.statusCode || 500);

const title = computed(() =>
  statusCode.value === 404
    ? 'error_titolo_404'
    : statusCode.value === 401
      ? 'error_titolo_401'
      : 'error_titolo_generale',
);
const subTitle = computed(() =>
  statusCode.value === 404
    ? 'error_sottotitolo_404'
    : statusCode.value === 401
      ? 'error_sottotitolo_401'
      : 'error_sottotitolo_generale',
);
const errorIcon = computed(() =>
  statusCode.value === 404
    ? 'fal fa-compass'
    : statusCode.value === 401
      ? 'fal fa-lock'
      : 'fal fa-triangle-exclamation',
);
const logoSrc = computed(() => (isDark.value ? '/logo.webp' : '/logo.webp'));

const checkToken = ref(false);
const menu = $localStorage.getItem('menu') ? JSON.parse($localStorage.getItem('menu')) : [];

// Logged-in admin → torna alla dashboard; altrimenti → home / login.
const destination = computed(() =>
  token && checkToken.value ? ((menu || [])[0] || {}).route || '/' : '/',
);
const buttonLabelKey = computed(() =>
  token && checkToken.value ? 'error_torna_pagina_iniziale' : 'error_effettua_login',
);

const retryPage = () => window.location.reload()

onMounted(async () => {
  const t = useCookie(`token${PROJECT_BASE}`).value;

  if (isString(t)) {
    await $fetch('/api/general/checkToken', {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + t,
      },
    })
      .then((res) => {
        checkToken.value = res;
      })
      .catch(() => {
        navigateTo('/');
      });
  } else {
    navigateTo('/');
  }
});
</script>
